import { useState } from 'react';

interface Segment {
  label: string;
  value: number;
  color: string;
  detail?: string;
}

/** Part-to-whole at a glance (≤ 6 segments), 2px surface gaps between arcs. */
export function Donut({ segments, size = 168, center, centerSub }: { segments: Segment[]; size?: number; center: string; centerSub: string }) {
  const [hover, setHover] = useState<number | null>(null);
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  const r = size / 2 - 8;
  const thick = 16;
  let angle = -Math.PI / 2;
  const arcs = segments.map((s, i) => {
    const a0 = angle;
    const a1 = angle + (s.value / total) * Math.PI * 2;
    angle = a1;
    const large = a1 - a0 > Math.PI ? 1 : 0;
    const cx = size / 2;
    const p = (a: number, rr: number) => `${(cx + rr * Math.cos(a)).toFixed(2)},${(cx + rr * Math.sin(a)).toFixed(2)}`;
    const d = `M${p(a0, r)}A${r},${r} 0 ${large} 1 ${p(a1, r)}L${p(a1, r - thick)}A${r - thick},${r - thick} 0 ${large} 0 ${p(a0, r - thick)}Z`;
    return { d, i };
  });
  const active = hover != null ? segments[hover] : null;
  return (
    <svg width={size} height={size} role="img" aria-label="Current split" className="donut">
      {arcs.map(({ d, i }) => (
        <path
          key={segments[i].label}
          d={d}
          fill={segments[i].color}
          stroke="var(--surface)"
          strokeWidth={2}
          opacity={hover == null || hover === i ? 1 : 0.45}
          onPointerEnter={() => setHover(i)}
          onPointerLeave={() => setHover(null)}
          tabIndex={0}
          onFocus={() => setHover(i)}
          onBlur={() => setHover(null)}
        >
          <title>{`${segments[i].label}: ${segments[i].detail ?? segments[i].value}`}</title>
        </path>
      ))}
      <text x="50%" y="47%" textAnchor="middle" className="donut-value">
        {active ? active.detail ?? active.value : center}
      </text>
      <text x="50%" y="60%" textAnchor="middle" className="donut-sub">
        {active ? active.label : centerSub}
      </text>
    </svg>
  );
}
